import React from 'react';

import { FEATURES } from 'appConstants';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { prizeComplete } from '../../adapters/common';
import { limitErrors } from '../../adapters/limits';
import StepPanel from '../../kit/StepPanel';
import { Checkbox } from '../../kit/controls';
import { Divider, Form, Line } from '../../kit/layout';
import { EnrolmentFields, LimitLine, ScheduleFields } from '../../kit/limitFields';
import { IWizardStepProps, newPrize, WizardLimits } from '../../wizardModel';
import PrizeList from '../PrizeList';

const TW = `${TOrbit}.wizard`;
const TL = `${TW}.limits`;

/** Step 4/5 of Instant Reward: the Game outcome cards without odds or a no-prize row. */
export const InstantPrizeStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <StepPanel
      step="prizes"
      title={t(`${TW}.setup.instant_outcome`)}
      question={t(`${TW}.prize.instantQuestion`)}
      errors={state.prizes.every(prizeComplete) ? [] : [t(`${TW}.prize.incomplete`)]}
      onNext={next}
      onBack={back}
    >
      <PrizeList
        addLabel={t(`${TW}.game.addOutcome`)}
        name="orbit-wizard-instant-prize"
        prizes={state.prizes}
        eyebrow={n => t(`${TW}.game.outcome`, { n })}
        blank={() => ({ ...newPrize(), slot: state.prizes.length + 1 })}
        onChange={prizes => update(current => ({ ...current, prizes }))}
      />
    </StepPanel>
  );
};

/**
 * Step 5/5 of Instant Reward on the Game 34 line pattern: four issuance limits, enrolment,
 * auto-redeem (behind the module's own flag) and the dates.
 */
export const InstantLimitsStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const flags = useFeatureFlags();
  const { limits } = state;
  const change = (patch: Partial<WizardLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...patch } }));

  return (
    <StepPanel
      step="limits"
      title={t(`${TW}.setup.instant_outcome`)}
      question={t(`${TL}.question`)}
      errors={limitErrors(state, t)}
      onNext={next}
      onBack={back}
    >
      <Form>
        <LimitLine
          on={limits.totalOn}
          onToggle={totalOn => change({ totalOn })}
          leadIn={t(`${TL}.issuanceTotal`)}
          value={limits.total}
          onChange={total => change({ total })}
          unit={t(`${TL}.rewardsUnit`)}
        />
        <LimitLine
          on={limits.perUserOn}
          onToggle={perUserOn => change({ perUserOn })}
          leadIn={t(`${TL}.issuancePerUser`)}
          value={limits.perUser}
          onChange={perUser => change({ perUser })}
          unit={t(`${TL}.rewardsUnit`)}
        />
        <LimitLine
          on={limits.rateOn}
          onToggle={rateOn => change({ rateOn })}
          leadIn={t(`${TL}.issuanceRate`)}
          value={limits.rate}
          onChange={rate => change({ rate })}
          unit={t(`${TL}.rewardsPer`)}
          period={{ value: limits.ratePeriod, onChange: ratePeriod => change({ ratePeriod }) }}
        />
        <LimitLine
          on={limits.userRateOn}
          onToggle={userRateOn => change({ userRateOn })}
          leadIn={t(`${TL}.issuanceUserRate`)}
          value={limits.userRate}
          onChange={userRate => change({ userRate })}
          unit={t(`${TL}.rewardsPer`)}
          period={{
            value: limits.userRatePeriod,
            onChange: userRatePeriod => change({ userRatePeriod })
          }}
        />
        <Divider />
        <EnrolmentFields limits={limits} onChange={change} withTime />
        {flags[FEATURES.AUTO_REDEEM_INSTANT_OUTCOME_TRANSACTION.key] === true ? (
          <Line>
            <Checkbox
              showLabel
              checked={limits.autoRedeem}
              label={t(`${TL}.autoRedeem`)}
              onChange={autoRedeem => change({ autoRedeem })}
            />
          </Line>
        ) : null}
        <ScheduleFields limits={limits} onChange={change} />
      </Form>
    </StepPanel>
  );
};
